// API Service for communicating with Firebase Functions
// This service provides access to AI-powered functions for data processing

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

export interface ColumnInfo {
  currentColumnName: string;
  allColumnNames: string[];
  // Additional context for better AI suggestions
  columnType?: string;
  existingPromptDetails?: string;
  existingFormatDetails?: string;
}

export interface RowData {
  id: string | number;
  [key: string]: any; // Dynamic column data
}

export interface ProcessedRowData {
  id: string | number;
  providedInput: Array<{
    headerName: string;
    content: string;
    columnKey: string;
  }>;
  expectedOutput: Array<{
    headerName: string;
    columnKey: string;
    promptDetails?: string;
    formatOptions?: string;
  }>;
}

// Response types for Firebase callable functions
export interface ApiResponse<T = any> {
  success: boolean;
  timestamp: string;
  data?: T;
  error?: string;
}

export interface ProcessResponse {
  success: boolean;
  timestamp: string;
  rowId: number | string;
  receivedData: any;
  processedData: any;
}

export interface FormatDetailsResponse {
  success: boolean;
  result: string;
}

// Create callable function references for existing functions only
const processDataFunction = httpsCallable<any, ProcessResponse>(functions, 'processData');
const formatDetailsFunction = httpsCallable<any, FormatDetailsResponse>(functions, 'formatDetails');

/**
 * Create format details for a column using AI
 * This function generates formatting instructions based on the column name and context
 */
export const createFormatDetails = async (columnInfo: ColumnInfo): Promise<ApiResponse<string>> => {
  try {
    const result = await formatDetailsFunction(columnInfo);
    
    if (result.data.success && result.data.result) {
      return {
        success: true,
        timestamp: new Date().toISOString(),
        data: result.data.result
      };
    } else {
      throw new Error('Invalid response format from formatDetails function');
    }
  } catch (error) {
    console.error('Error calling formatDetails function:', error);
    
    // Provide specific error messages for common issues
    if (error instanceof Error) {
      if (error.message.includes('CORS')) {
        console.error('CORS Error - Firebase Functions emulator may not be running');
        console.error('Run: cd functions && firebase emulators:start --only functions');
        return {
          success: false,
          timestamp: new Date().toISOString(),
          error: 'Firebase Functions emulator not running. Please start it with: firebase emulators:start --only functions'
        };
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        return {
          success: false,
          timestamp: new Date().toISOString(),
          error: 'Network error. Please check your connection and Firebase Functions emulator.'
        };
      } else if (error.message.includes('not-found')) {
        return {
          success: false,
          timestamp: new Date().toISOString(),
          error: 'formatDetails function not found. Please check your Firebase Functions deployment.'
        };
      }
    }
    
    return {
      success: false,
      timestamp: new Date().toISOString(),
      error: error instanceof Error ? error.message : 'Failed to generate format details'
    };
  }
};

/**
 * Process row data using AI to fill empty columns
 * This function takes a row with some filled columns and fills the empty ones using AI
 */
export const processRowData = async (rowData: RowData): Promise<ApiResponse<any>> => {
  try {
    const result = await processDataFunction(rowData);
    
    if (result.data.success) {
      return {
        success: true,
        timestamp: new Date().toISOString(),
        data: result.data
      };
    } else {
      throw new Error('Processing failed');
    }
  } catch (error) {
    console.error('Error calling processData function:', error);
    return {
      success: false,
      timestamp: new Date().toISOString(),
      error: error instanceof Error ? error.message : 'Failed to process row data'
    };
  }
};

// Export utility functions for data transformation
export const transformRowDataForApi = (
  rowData: any,
  columnConfigs: Array<{ id: string | number; name: string; options: any }>
): RowData => {
  const transformed: RowData = {
    id: rowData.id || Date.now()
  };

  // Transform each column
  columnConfigs.forEach((config) => {
    const columnKey = `col${config.id}`;
    transformed[columnKey] = {
      headerName: config.name,
      content: rowData[config.id] || '',
      promptDetails: config.options.promptDetails || '',
      formatOptions: config.options.formatDetails || ''
    };
  });

  return transformed;
};

export const transformApiResponseToRowData = (
  apiResponse: any,
  columnConfigs: Array<{ id: string | number; name: string }>
): any => {
  if (!apiResponse.processedData?.filledColumns) {
    return {};
  }

  const result: any = {};
  
  // Transform filled columns back to row data format
  Object.entries(apiResponse.processedData.filledColumns).forEach(([columnKey, columnData]: [string, any]) => {
    // Extract column ID from column key (e.g., "col1" -> "1")
    const columnId = columnKey.replace('col', '');
    result[columnId] = columnData.content;
  });

  return result;
};

// Backward compatibility - export the functions with the same names as ag_grid
export const crateFormatDetails = createFormatDetails;
export const processData = processRowData;

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  createFormatDetails,
  processRowData,
  transformRowDataForApi,
  transformApiResponseToRowData,
  // Backward compatibility
  crateFormatDetails,
  processData
}; 